import Dialog from "../../General/ConfirmableDialog";
import React, { useState } from "react";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";

interface GenerateBillOfSaleModalProps {
  open: boolean;
  onClose: () => void;
}

const GenerateBillOfSaleModal: React.FC<GenerateBillOfSaleModalProps> = ({
  open,
  onClose,
}) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    SellerCompanyName: "",
    BuyerCompanyName: "",
    SellerAddress: "",
    SellerPhone: "",
    BuyerAddress: "",
    BuyerPhone: "",
    InvoiceNumber: "",
    InvoiceDate: new Date().toISOString().substr(0, 10),
    Finance: "",
    SalesRep: "",
    Tax: 0,
    TitleAdminFees: 0,
    DownPayment: 0,
    TotalAmount: 0,
    Items: [
      {
        Year: "",
        Make: "",
        Model: "",
        VinNumber: "",
        StockNumber: "",
        Color: "",
        PricePerUnit: 0,
      },
    ],
  });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleInputChange = (e: {
    target: { type?: any; name?: any; value?: any };
  }) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: e.target.type === "number" ? parseFloat(value) || 0 : value,
    }));
  };

  const handleItemChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newItems = [...prev.Items];
      newItems[index] = {
        ...newItems[index],
        [name]: e.target.type === "number" ? parseFloat(value) || 0 : value,
      };
      return { ...prev, Items: newItems };
    });
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      Items: [
        ...prev.Items,
        {
          Year: "",
          Make: "",
          Model: "",
          VinNumber: "",
          StockNumber: "",
          Color: "",
          PricePerUnit: 0,
        },
      ],
    }));
  };

  const removeItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      Items: prev.Items.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${apiVariable}/generateBillOfSale`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "BillOfSale.pdf");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      onClose();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        setErrorMessage(
          error.response.data || "Failed to generate Bill of Sale"
        );
      } else {
        setErrorMessage("Failed to generate Bill of Sale");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Enter Bill of Sale Information</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            margin="dense"
            label="Seller Company Name"
            name="SellerCompanyName"
            fullWidth
            value={formData.SellerCompanyName}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Buyer Company Name"
            name="BuyerCompanyName"
            fullWidth
            value={formData.BuyerCompanyName}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Seller Address"
            name="SellerAddress"
            fullWidth
            value={formData.SellerAddress}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Seller Phone"
            name="SellerPhone"
            fullWidth
            value={formData.SellerPhone}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Buyer Address"
            name="BuyerAddress"
            fullWidth
            value={formData.BuyerAddress}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Buyer Phone"
            name="BuyerPhone"
            fullWidth
            value={formData.BuyerPhone}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Invoice Number"
            name="InvoiceNumber"
            fullWidth
            value={formData.InvoiceNumber}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Invoice Date"
            name="InvoiceDate"
            type="date"
            fullWidth
            value={formData.InvoiceDate}
            onChange={handleInputChange}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            margin="dense"
            label="Finance"
            name="Finance"
            fullWidth
            value={formData.Finance}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Sales Rep"
            name="SalesRep"
            fullWidth
            value={formData.SalesRep}
            onChange={handleInputChange}
          />

          <TextField
            margin="dense"
            label="Tax"
            name="Tax"
            type="number"
            fullWidth
            value={formData.Tax}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Title/Admin Fees"
            name="TitleAdminFees"
            type="number"
            fullWidth
            value={formData.TitleAdminFees}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Down Payment"
            name="DownPayment"
            type="number"
            fullWidth
            value={formData.DownPayment}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Total Amount"
            name="TotalAmount"
            type="number"
            fullWidth
            value={formData.TotalAmount}
            onChange={handleInputChange}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Items
          </Typography>
          {formData.Items.map((item, index) => (
            <Box
              key={index}
              sx={{
                border: "1px solid #ccc",
                borderRadius: 1,
                p: 2,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="subtitle1">Item {index + 1}</Typography>
                <IconButton
                  onClick={() => removeItem(index)}
                  disabled={formData.Items.length === 1}
                >
                  <RemoveCircleOutline />
                </IconButton>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                <TextField
                  label="Year"
                  name="Year"
                  value={item.Year}
                  onChange={(e) => handleItemChange(index, e)}
                  margin="dense"
                />
                <TextField
                  label="Make"
                  name="Make"
                  value={item.Make}
                  onChange={(e) => handleItemChange(index, e)}
                  margin="dense"
                />
                <TextField
                  label="Model"
                  name="Model"
                  value={item.Model}
                  onChange={(e) => handleItemChange(index, e)}
                  margin="dense"
                />
                <TextField
                  label="VIN Number"
                  name="VinNumber"
                  value={item.VinNumber}
                  onChange={(e) => handleItemChange(index, e)}
                  margin="dense"
                />
                <TextField
                  label="Stock Number"
                  name="StockNumber"
                  value={item.StockNumber}
                  onChange={(e) => handleItemChange(index, e)}
                  margin="dense"
                />
                <TextField
                  label="Color"
                  name="Color"
                  value={item.Color}
                  onChange={(e) => handleItemChange(index, e)}
                  margin="dense"
                />
                <TextField
                  label="Price Per Unit"
                  name="PricePerUnit"
                  type="number"
                  value={item.PricePerUnit}
                  onChange={(e) => handleItemChange(index, e)}
                  margin="dense"
                />
              </Box>
            </Box>
          ))}
          <Button
            variant="outlined"
            startIcon={<AddCircleOutline />}
            onClick={addItem}
          >
            Add Item
          </Button>
        </Box>

        {errorMessage && (
          <Typography color="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateBillOfSaleModal;
